import React, { useCallback } from 'react'
import { makeStyles } from '@material-ui/styles'
import Tooltip from 'core/elements/tooltip'
import { topMiddle } from 'pf9-ui-components/built/elements/menu/defaults'
import Text from 'pf9-ui-components/built/elements/Text'
import FontAwesomeIcon from 'pf9-ui-components/built/components/FontAwesomeIcon'
import Theme from 'pf9-ui-components/built/theme-manager/themes/model'
import withFormContext, {
  PropsWithFormContext,
} from 'pf9-ui-components/built/components/validatedForm/withFormContext'
import clsx from 'clsx'
import colors from 'core/themes/base/colors'

interface MultilineTextFieldProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  id?: string
  errorMessage?: string
  required?: boolean
  info?: React.ReactNode
  returnAsString?: boolean
  type?: string
}

const useStyles = makeStyles<Theme, Partial<MultilineTextFieldProps>>((theme) => ({
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: theme.spacing(1),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  label: {
    color: theme.components.input.label.color,
  },
  minus: {
    fontWeight: 900,
    color: theme.palette.blue.main,
    justifySelf: 'end',
  },
  hint: {
    cursor: 'help',
    transition: 'color .2s ease',
    color: theme.components.input.label.hint,
    '& i': {
      cursor: 'help',
      marginRight: 8,
      transition: 'color .2s ease',
      color: theme.components.input.label.hint,
    },
  },
  textArea: {
    width: '100%',
    minHeight: 140,
    resize: 'vertical',
    whiteSpace: 'pre-wrap',
    boxSizing: 'border-box',
    borderWidth: 1,
    cursor: 'pointer',
    pointerEvents: 'unset',
    borderColor: ({ errorMessage }) =>
      !!errorMessage ? theme.components.input.error : theme.components.iconButton.activeBorder,
    display: 'grid',
    gridTemplateColumns: 'unset',
    padding: '6px 10px',
    margin: 0,
    fontFamily: 'Eina04',
    fontSize: '14px',
    fontWeight: 'normal',
    borderRadius: 4,
    color: theme.components.code.text,
    wordBreak: 'break-all',
    overflow: 'auto',
    '& *': {
      fontFamily: 'Eina04',
    },
    '&:focus-visible': {
      outline: 'none',
      borderColor: colors.grey[300],
    },
  },
  error: {
    marginLeft: 4,
    gridArea: 'input-error',
    color: theme.components.input.error,
  },
}))

const MultilineTextField = ({
  value,
  label,
  id,
  required,
  errorMessage,
  info,
  onChange,
  type,
  returnAsString,
  className,
  ...restProps
}: PropsWithFormContext<string, MultilineTextFieldProps>) => {
  const classes = useStyles({ info, errorMessage })
  const handleChange = useCallback(
    (e) => {
      // HTML specs says that <input type="number"> return strings but it's more useful if we
      // convert it to a `Number` to reduce type casting all over the place.
      const strVal = e.target.value
      const value =
        type && type.toLowerCase() === 'number' && strVal !== '' ? Number(strVal) : strVal
      const returnValue = returnAsString ? String(value) : value

      if (onChange) {
        onChange(returnValue)
      }
    },
    [onChange, type],
  )

  return (
    <div className={clsx(classes.container, className)}>
      <div className={classes.header}>
        {label && (
          <Text variant="inputLabel" className={clsx(classes.label, 'label')}>
            {label}
            {required && ' *'}
          </Text>
        )}
        {info && (
          <Tooltip message={info || label} className={classes.info}>
            <Text variant="inputLabel" className={classes.hint}>
              <FontAwesomeIcon>question-circle</FontAwesomeIcon>Hint
            </Text>
          </Tooltip>
        )}
      </div>
      <textarea
        value={[undefined, ''].includes(value) ? '' : type === 'number' ? Number(value) : value}
        onChange={handleChange}
        className={classes.textArea}
        {...restProps}
        id={`${id}-field`}
        style={{ scrollMarginTop: '40px' }}
      />
      {!!errorMessage && (
        <div>
          <Text variant="body2" className={classes.error}>
            {errorMessage}
          </Text>
        </div>
      )}
    </div>
  )
}

export default withFormContext(MultilineTextField)
